/**
 * MHContainer - Mindhunters Governance-Compliant Container Component
 *
 * Layer 3: MH Components (Governance Layer)
 * - Maps to Room System states
 * - Enforces visual hierarchy
 *
 * Container types (from FRONTEND_GOVERNANCE):
 * - page: OBSERVE state (default view/navigation)
 * - panel: ANALYZE state (comparing/evaluating)
 * - room: DECIDE state (commit action with isolation)
 */

'use client';

import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const mhContainerVariants = cva(
  'w-full',
  {
    variants: {
      type: {
        // Page: OBSERVE - main content area
        page: 'bg-bg-0 text-text-primary',

        // Panel: ANALYZE - elevated surface
        panel: 'bg-surface-1 rounded-2xl p-6',

        // Room: DECIDE - isolated with backdrop
        room: 'bg-surface-2 rounded-2xl p-8',
      },
    },
    defaultVariants: {
      type: 'page',
    },
  }
);

export interface MHContainerProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof mhContainerVariants> {}

const MHContainer = React.forwardRef<HTMLDivElement, MHContainerProps>(
  ({ className, type, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(mhContainerVariants({ type, className }))}
        {...props}
      />
    );
  }
);

MHContainer.displayName = 'MHContainer';

export { MHContainer, mhContainerVariants };
